import { entryHours, isoDate } from '../../lib/time.js';

export function monthStart(date) {
  return isoDate(new Date(date.getFullYear(), date.getMonth(), 1));
}

export function toggleValue(values, value) {
  return values.includes(value)
    ? values.filter((item) => item !== value)
    : [...values, value];
}

export function filterProjectsByClients(projects, clientIds) {
  if (!clientIds.length) return projects;
  return projects.filter((project) => clientIds.includes(project.clientId));
}

export function filterUsersByTeams(users, teamIds) {
  if (!teamIds.length) return users;
  return users.filter((user) => teamIds.includes(user.teamId));
}

export function keepVisibleSelections(selectedIds, visibleItems) {
  const visibleIds = new Set(visibleItems.map((item) => item.id));
  return selectedIds.filter((id) => visibleIds.has(id));
}

const clientCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

export function clientRowValue(row, key) {
  if (key === 'client') return row.client.name;
  if (key === 'clientWork')
    return row.hours ? row.billableHours / row.hours : 0;
  if (key === 'projects') return row.projects?.size ?? row.projectCount;
  return row.hours;
}

export function sortClientRows(rows, sort) {
  return [...rows].sort((a, b) => {
    const left = clientRowValue(a, sort.key);
    const right = clientRowValue(b, sort.key);
    const result =
      typeof left === 'number' && typeof right === 'number'
        ? left - right
        : clientCollator.compare(String(left), String(right));
    return sort.direction === 'asc' ? result : -result;
  });
}

// Absence sections, paid apart from unpaid: only the paid one is part of the
// total the shares are taken of.
const ABSENCE_SECTIONS = [
  { id: 'paid-absence', label: 'Paid absence' },
  { id: 'unpaid-absence', label: 'Unpaid absence · not in total' },
];

function absenceSectionOf(project) {
  return project.absencePaid === false ? 'unpaid-absence' : 'paid-absence';
}

// Absence projects get sections of their own, so vacation never reads as a
// new or support project.
export function buildProjectSections(rows) {
  const sectionOf = (row) =>
    row.project.absence
      ? absenceSectionOf(row.project)
      : row.project.supportProject
        ? 'support'
        : 'new';
  return [
    { id: 'new', label: 'New projects' },
    { id: 'support', label: 'Support projects' },
    ...ABSENCE_SECTIONS,
  ]
    .map((section) => {
      const sectionRows = rows.filter((row) => sectionOf(row) === section.id);
      return {
        id: section.id,
        label: section.label,
        rows: sectionRows,
        hours: sectionRows.reduce((sum, row) => sum + row.hours, 0),
      };
    })
    .filter((section) => section.rows.length);
}

function clientSection(rows, { id, label, itemLabel = 'client' }) {
  return {
    id,
    label,
    itemLabel,
    rows,
    hours: rows.reduce((sum, row) => sum + row.hours, 0),
    billableHours: rows.reduce((sum, row) => sum + row.billableHours, 0),
    projectCount: rows.reduce(
      (sum, row) => sum + (row.projects?.size ?? row.projectCount ?? 0),
      0,
    ),
  };
}

function clientRowsForProjectType(clientRows, projectRows, supportProject) {
  const externalClients = new Map(
    clientRows.map((row, index) => [
      row.client.id,
      { client: row.client, index },
    ]),
  );
  const grouped = new Map();

  projectRows.forEach((projectRow) => {
    const client = externalClients.get(projectRow.project.clientId);
    if (
      !client ||
      Boolean(projectRow.project.supportProject) !== supportProject
    )
      return;
    const row = grouped.get(client.client.id) || {
      client: client.client,
      index: client.index,
      hours: 0,
      billableHours: 0,
      projectCount: 0,
    };
    row.hours += projectRow.hours;
    row.billableHours += projectRow.billableHours;
    row.projectCount += 1;
    grouped.set(client.client.id, row);
  });

  return [...grouped.values()]
    .sort((a, b) => a.index - b.index)
    .map(({ index: _index, ...row }) => row);
}

export function buildClientSections(rows, projectRows = []) {
  const externalRows = rows.filter((row) => !row.client.name.startsWith('_'));
  const internalRows = rows.filter((row) => row.client.name.startsWith('_'));
  const external = clientSection(externalRows, {
    id: 'external',
    label: 'External clients',
  });

  if (projectRows.length) {
    external.subsections = [
      clientSection(
        clientRowsForProjectType(externalRows, projectRows, false),
        { id: 'new', label: 'New projects' },
      ),
      clientSection(clientRowsForProjectType(externalRows, projectRows, true), {
        id: 'support',
        label: 'Support projects',
      }),
    ].filter((section) => section.rows.length);
  }

  return [
    external,
    clientSection(internalRows, {
      id: 'internal',
      label: 'Internal',
      itemLabel: 'category',
    }),
    ...ABSENCE_SECTIONS.map(({ id, label }) =>
      clientSection(absenceRows(projectRows, id), {
        id,
        label,
        itemLabel: 'project',
      }),
    ),
  ].filter((section) => section.rows.length);
}

// Absence is nobody's client work, so the server leaves it out of the client
// rows; the By client table still shows it, from the project rows — one row
// per absence project (Vacation, Sickness…) under its paid or unpaid section,
// linking nowhere.
function absenceRows(projectRows, section) {
  return projectRows
    .filter(
      (row) => row.project.absence && absenceSectionOf(row.project) === section,
    )
    .map((row) => ({
      client: { id: `absence:${row.project.id}`, name: row.project.name },
      hours: row.hours,
      billableHours: 0,
      projectCount: 1,
    }));
}

export function buildSummary(entries, data) {
  const totalHours = entries.reduce((sum, entry) => sum + entryHours(entry), 0);
  const billableHours = entries
    .filter((entry) => entry.billable)
    .reduce((sum, entry) => sum + entryHours(entry), 0);
  return {
    totalHours,
    billableHours,
    billablePct: totalHours ? (billableHours / totalHours) * 100 : 0,
    projectCount: new Set(entries.map((entry) => entry.projectId)).size,
    userCount: new Set(entries.map((entry) => entry.userId)).size,
    clientCount: new Set(
      entries
        .map(
          (entry) =>
            data.allProjects.find((project) => project.id === entry.projectId)
              ?.clientId,
        )
        .filter(Boolean),
    ).size,
  };
}

export function buildProjectRows(entries, data) {
  return Object.values(
    entries.reduce((acc, entry) => {
      const project = data.allProjects.find(
        (item) => item.id === entry.projectId,
      );
      if (!project) return acc;
      acc[project.id] ||= {
        project,
        hours: 0,
        billableHours: 0,
        people: new Set(),
      };
      acc[project.id].hours += entryHours(entry);
      if (entry.billable) acc[project.id].billableHours += entryHours(entry);
      acc[project.id].people.add(entry.userId);
      return acc;
    }, {}),
  ).sort((a, b) => b.hours - a.hours);
}

export function buildPeopleRows(entries, data) {
  return Object.values(
    entries.reduce((acc, entry) => {
      const user = data.users.find((item) => item.id === entry.userId) || {
        id: entry.userId,
        name: entry.userName,
        initials: '?',
        color: '#64748b',
      };
      acc[entry.userId] ||= {
        user,
        hours: 0,
        billableHours: 0,
        projects: new Set(),
      };
      acc[entry.userId].hours += entryHours(entry);
      if (entry.billable) acc[entry.userId].billableHours += entryHours(entry);
      acc[entry.userId].projects.add(entry.projectId);
      return acc;
    }, {}),
  ).sort((a, b) => b.hours - a.hours);
}

export function buildClientRows(entries, data) {
  return Object.values(
    entries.reduce((acc, entry) => {
      const project = data.allProjects.find(
        (item) => item.id === entry.projectId,
      );
      const client = data.clients.find((item) => item.id === project?.clientId);
      if (!client) return acc;
      acc[client.id] ||= {
        client,
        hours: 0,
        billableHours: 0,
        projects: new Set(),
      };
      acc[client.id].hours += entryHours(entry);
      if (entry.billable) acc[client.id].billableHours += entryHours(entry);
      acc[client.id].projects.add(entry.projectId);
      return acc;
    }, {}),
  ).sort((a, b) => b.hours - a.hours);
}

// The member detail's "Clients in period" panel: who the person actually
// worked for, each client expandable to the projects behind it, plus one
// bucket for everything non-billable. Billable hours are what makes a client
// a client here — an internal project lands in the non-client bucket whatever
// client carries it, so the two never double-count the same hour.
export function buildMemberClientRows(clientRows, projectRows) {
  const projectsByClient = new Map();
  let nonClientHours = 0;
  const nonClientNames = new Set();

  for (const row of projectRows) {
    if (!row.hours) continue;
    if (!row.project.billable) {
      nonClientHours += row.hours;
      if (row.project.clientName) nonClientNames.add(row.project.clientName);
      continue;
    }
    const list = projectsByClient.get(row.project.clientId) || [];
    list.push(row);
    projectsByClient.set(row.project.clientId, list);
  }

  const clients = clientRows
    .filter((row) => row.billableHours > 0)
    .map((row) => ({
      client: row.client,
      hours: row.billableHours,
      projects: [...(projectsByClient.get(row.client.id) || [])].sort(
        (a, b) => b.hours - a.hours,
      ),
    }))
    .sort((a, b) => b.hours - a.hours);

  return {
    clients,
    nonClient: {
      hours: nonClientHours,
      clientNames: [...nonClientNames].sort(),
    },
  };
}
